import type { QuestionBody } from '../../domain/entities/content.entity';
import { isAnnulled } from '../answer-key';

/**
 * As três marcações editoriais da questão — **anulada**, **passível de recurso**
 * e **desatualizada** — como badges.
 *
 * São um conjunto, não três casos soltos: as três são afirmações do time de
 * conteúdo *sobre* a questão (não mudam o que ela pergunta), as três são
 * booleanos opcionais no `body`, e as três precisam ser vistas de relance
 * varrendo uma prova de 80 questões. Uma peça só é o que impede a terceira badge
 * de nascer com outra forma no dia em que aparecer uma quarta flag.
 *
 * Mesma forma, hues distantes entre si: âmbar, violeta e rosa. Nenhuma usa o
 * verde de "alternativa correta" nem o vermelho de ação destrutiva — são
 * rótulos, não estado de erro.
 *
 * Usado em **dois** lugares, e em nenhum dos dois duas vezes: o header do card
 * (varredura da prova) e a aba Preview do form, que não tem header — é ela que
 * faz a marcação aparecer na hora, antes de salvar. O `QuestionBodyView` de
 * propósito NÃO as renderiza: no card em leitura ele fica logo abaixo do
 * header, e a mesma marcação saía duas vezes coladas uma na outra.
 */

const BADGE_BASE = 'rounded px-1.5 py-0.5 text-xs font-medium';

const STYLES = {
  annulled:
    'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  appealable:
    'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400',
  outdated: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400',
} as const;

const LABELS = {
  annulled: {
    short: 'Anulada',
    title:
      'A banca anulou esta questão — não há alternativa correta a considerar',
  },
  appealable: {
    short: 'Passível de recurso',
    title:
      'Cabe recurso contra esta questão (gabarito duvidoso ou enunciado ambíguo)',
  },
  outdated: {
    short: 'Desatualizada',
    title: 'A conduta ou diretriz mudou desde que esta prova foi aplicada',
  },
} as const;

/** As flags ligadas de um body, na ordem fixa em que as badges aparecem. */
export function questionFlags(
  body: QuestionBody | null | undefined,
): (keyof typeof LABELS)[] {
  if (!body) return [];
  return [
    // `isAnnulled` existe como função por causa da união de formatos (o campo só
    // está no ramo MULTIPLE_CHOICE); os outros dois estão nos dois ramos, então
    // são lidos direto — um wrapper para cada seria cerimônia sem ganho.
    ...(isAnnulled(body) ? (['annulled'] as const) : []),
    ...(body.appealable === true ? (['appealable'] as const) : []),
    ...(body.outdated === true ? (['outdated'] as const) : []),
  ];
}

export function QuestionFlagBadges({
  body,
}: {
  body: QuestionBody | null | undefined;
}) {
  const flags = questionFlags(body);
  if (flags.length === 0) return null;

  return (
    <>
      {flags.map((flag) => (
        <span
          key={flag}
          title={LABELS[flag].title}
          className={`${BADGE_BASE} ${STYLES[flag]}`}
        >
          {LABELS[flag].short}
        </span>
      ))}
    </>
  );
}
